import React, { useState, useEffect } from 'react';
import { 
  Card, Tabs, List, Avatar, Button, Empty, Spin, Input, 
  Tooltip, Popconfirm, Badge, Tag, Segmented, Drawer, notification 
} from 'antd';
import { 
  InboxOutlined, 
  BellOutlined, 
  CheckOutlined, 
  DeleteOutlined, 
  SendOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseOutlined,
  FileTextOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import notificationService from '../services/notificationService';
import messageService from '../services/messageService';
import MessageDetail from './MessageDetail';

const { TabPane } = Tabs;
const { Search } = Input;

const Inbox = () => {
  // State for notifications
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsTotal, setNotificationsTotal] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  
  // State for messages
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  
  // Message detail drawer state
  const [messageDetailVisible, setMessageDetailVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // Filters and search
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('notifications');
  const [notificationViewType, setNotificationViewType] = useState('all');
  const [messageViewType, setMessageViewType] = useState('all');

  // Fetch data on component mount
  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchNotifications();
    } else {
      fetchMessages();
    }
  }, [activeTab, notificationsPage, messagesPage, notificationViewType, messageViewType]);

  // Handle search effect
  useEffect(() => {
    if (searchText.length >= 2) {
      if (activeTab === 'notifications') {
        fetchNotifications(1, searchText);
      } else {
        fetchMessages(1, searchText);
      }
    } else if (searchText === '') {
      if (activeTab === 'notifications') {
        fetchNotifications(1);
      } else {
        fetchMessages(1);
      }
    }
  }, [searchText]);

  // Fetch notifications with pagination, search and filters
  const fetchNotifications = async (page = notificationsPage, search = searchText) => {
    try {
      setNotificationsLoading(true);
      
      const params = {
        page,
        search,
        type: notificationViewType !== 'all' ? notificationViewType : undefined,
        read: notificationViewType === 'unread' ? 0 : (notificationViewType === 'read' ? 1 : undefined)
      };
      
      const response = await notificationService.getNotifications(params);
      
      setNotifications(response.notifications || []);
      setNotificationsTotal(response.total || 0);
      setUnreadNotificationsCount(response.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      notification.error({
        message: 'Failed to load notifications',
        description: error.message || 'Please try again later'
      });
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Fetch messages with pagination, search and filters
  const fetchMessages = async (page = messagesPage, search = searchText) => {
    try {
      setMessagesLoading(true);
      
      const params = {
        page,
        search,
        read: messageViewType === 'unread' ? 0 : (messageViewType === 'read' ? 1 : undefined)
      };
      
      const response = await messageService.getMessages(params);
      
      setMessages(response.messages || []);
      setMessagesTotal(response.total || 0);
      setUnreadMessagesCount(response.unread_count || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
      notification.error({
        message: 'Failed to load messages',
        description: error.message || 'Please try again later'
      });
    } finally {
      setMessagesLoading(false);
    }
  };

  // Mark a notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true } 
            : notif
        )
      );
      
      // Decrease unread count
      setUnreadNotificationsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark a message as read
  const markMessageAsRead = async (messageId) => {
    try {
      await messageService.markAsRead(messageId);
      
      // Update local state
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, is_read: true } 
            : msg
        )
      );
      
      // Decrease unread count
      setUnreadMessagesCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notif => ({ ...notif, is_read: true }))
      );
      
      // Reset unread count
      setUnreadNotificationsCount(0);
      
      notification.success({
        message: 'All notifications marked as read',
        placement: 'bottomRight'
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Mark all messages as read
  const markAllMessagesAsRead = async () => {
    try {
      await messageService.markAllAsRead();
      
      // Update local state
      setMessages(prevMessages => 
        prevMessages.map(msg => ({ ...msg, is_read: true }))
      );
      
      // Reset unread count
      setUnreadMessagesCount(0);
      
      notification.success({
        message: 'All messages marked as read',
        placement: 'bottomRight'
      });
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  // Delete a notification
  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Remove from local state
      setNotifications(prevNotifications => 
        prevNotifications.filter(notif => notif.id !== notificationId)
      );
      
      notification.success({
        message: 'Notification deleted',
        placement: 'bottomRight'
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Delete a message
  const deleteMessage = async (messageId) => {
    try {
      await messageService.deleteMessage(messageId);
      
      // Remove from local state
      setMessages(prevMessages => 
        prevMessages.filter(msg => msg.id !== messageId)
      );
      
      notification.success({
        message: 'Message deleted',
        placement: 'bottomRight'
      });
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // View message detail
  const viewMessageDetail = (message) => {
    setSelectedMessage(message);
    setMessageDetailVisible(true);
    
    // Mark as read if unread
    if (!message.is_read) {
      markMessageAsRead(message.id);
    }
  };

  // Format notification/message date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const isToday = date >= today;
    const isYesterday = date >= yesterday && date < today;
    
    if (isToday) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (isYesterday) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString([], { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Get icon for notification
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageOutlined style={{ color: '#1890ff' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'info':
      default:
        return <InfoCircleOutlined style={{ color: '#52c41a' }} />;
    }
  };

  // Notification list render
  const renderNotificationsList = () => {
    return (
      <>
        <div className="flex justify-between items-center mb-4">
          <Segmented
            options={[
              { label: 'All', value: 'all' },
              { label: <><Badge status="processing" />Unread</>, value: 'unread' },
              { label: 'Read', value: 'read' }
            ]}
            value={notificationViewType}
            onChange={setNotificationViewType}
          />
          
          {unreadNotificationsCount > 0 && (
            <Button 
              type="primary" 
              size="small" 
              onClick={markAllNotificationsAsRead}
              icon={<CheckOutlined />}
              className="bg-golden-navy"
            >
              Mark all as read
            </Button>
          )}
        </div>

        {notificationsLoading ? (
          <div className="flex justify-center py-8">
            <Spin size="large" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty 
            description="No notifications found" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="py-8"
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={notifications}
            pagination={{
              current: notificationsPage,
              pageSize: 10,
              total: notificationsTotal,
              onChange: (page) => setNotificationsPage(page),
              showSizeChanger: false
            }}
            renderItem={item => (
              <List.Item 
                className={`border-b ${!item.is_read ? 'bg-blue-50' : ''}`}
                actions={[
                  !item.is_read && (
                    <Tooltip title="Mark as read">
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<CheckOutlined />} 
                        onClick={() => markNotificationAsRead(item.id)}
                        className="text-blue-500"
                      />
                    </Tooltip>
                  ),
                  <Tooltip title="Delete">
                    <Popconfirm
                      title="Are you sure you want to delete this notification?"
                      onConfirm={() => deleteNotification(item.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button 
                        type="text" 
                        size="small" 
                        danger
                        icon={<DeleteOutlined />} 
                      />
                    </Popconfirm>
                  </Tooltip>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      icon={getNotificationIcon(item.type)} 
                      className={`${!item.is_read ? 'bg-blue-100' : 'bg-gray-100'}`}
                    />
                  }
                  title={
                    <div className="flex justify-between">
                      <span className={`${!item.is_read ? 'font-semibold' : ''}`}>
                        {item.title}
                      </span>
                      <Tag 
                        color={item.type === 'warning' ? 'orange' : (item.type === 'message' ? 'blue' : 'green')}
                        className="text-xs"
                      >
                        {item.type === 'warning' ? 'Warning' : (item.type === 'message' ? 'Message' : 'Info')}
                      </Tag>
                    </div>
                  }
                  description={
                    <>
                      <div className="text-sm mb-1">{item.message}</div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{formatDate(item.created_at)}</span>
                        {item.link && (
                          <Link 
                            to={item.link}
                            className="text-blue-500"
                            onClick={() => markNotificationAsRead(item.id)}
                          >
                            View details
                          </Link>
                        )}
                      </div>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </>
    );
  };

  // Messages list render
  const renderMessagesList = () => {
    return (
      <>
        <div className="flex justify-between items-center mb-4">
          <Segmented
            options={[
              { label: 'All', value: 'all' },
              { label: <><Badge status="processing" />Unread</>, value: 'unread' },
              { label: 'Read', value: 'read' }
            ]}
            value={messageViewType}
            onChange={setMessageViewType}
          />
          
          {unreadMessagesCount > 0 && (
            <Button 
              type="primary" 
              size="small" 
              onClick={markAllMessagesAsRead}
              icon={<CheckOutlined />}
              className="bg-golden-navy"
            >
              Mark all as read
            </Button>
          )}
        </div>

        {messagesLoading ? (
          <div className="flex justify-center py-8">
            <Spin size="large" />
          </div>
        ) : messages.length === 0 ? (
          <Empty 
            description="No messages found" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="py-8"
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={messages}
            pagination={{
              current: messagesPage,
              pageSize: 10,
              total: messagesTotal,
              onChange: (page) => setMessagesPage(page),
              showSizeChanger: false
            }}
            renderItem={item => (
              <List.Item 
                className={`border-b cursor-pointer hover:bg-gray-50 ${!item.is_read ? 'bg-blue-50' : ''}`}
                onClick={() => viewMessageDetail(item)}
                actions={[
                  !item.is_read && (
                    <Tooltip title="Mark as read">
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<CheckOutlined />} 
                        onClick={(e) => {
                          e.stopPropagation();
                          markMessageAsRead(item.id);
                        }}
                        className="text-blue-500"
                      />
                    </Tooltip>
                  ),
                  <Tooltip title="Delete">
                    <Popconfirm
                      title="Are you sure you want to delete this message?"
                      onConfirm={(e) => {
                        e.stopPropagation();
                        deleteMessage(item.id);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button 
                        type="text" 
                        size="small" 
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => e.stopPropagation()} 
                      />
                    </Popconfirm>
                  </Tooltip>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      icon={<UserOutlined />} 
                      src={item.sender_avatar}
                      className={`${!item.is_read ? 'bg-blue-100' : 'bg-gray-100'}`}
                    />
                  }
                  title={
                    <div className="flex justify-between">
                      <span className={`${!item.is_read ? 'font-semibold' : ''}`}>
                        {item.subject || 'No Subject'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                  }
                  description={
                    <>
                      <div className="text-xs text-gray-600 mb-1">
                        From: {item.sender_name || 'System'}
                      </div>
                      <div className="text-sm truncate">{item.message}</div>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Inbox</h1>
      </div>

      <Card className="shadow-md mb-6">
        <div className="mb-4">
          <Search
            placeholder="Search..."
            allowClear
            enterButton
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
          />
        </div>

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          tabBarExtraContent={
            <div className="flex">
              <Badge count={unreadNotificationsCount} offset={[0, 0]} style={{ display: activeTab === 'notifications' ? 'block' : 'none' }}>
                <Button 
                  type={activeTab === 'notifications' ? 'primary' : 'default'}
                  icon={<BellOutlined />}
                  onClick={() => setActiveTab('notifications')}
                  className={activeTab === 'notifications' ? 'bg-golden-navy' : ''}
                >
                  Notifications
                </Button>
              </Badge>
              <div className="w-2"></div>
              <Badge count={unreadMessagesCount} offset={[0, 0]} style={{ display: activeTab === 'messages' ? 'block' : 'none' }}>
                <Button 
                  type={activeTab === 'messages' ? 'primary' : 'default'}
                  icon={<MessageOutlined />}
                  onClick={() => setActiveTab('messages')}
                  className={activeTab === 'messages' ? 'bg-golden-navy' : ''}
                >
                  Messages
                </Button>
              </Badge>
            </div>
          }
        >
          <TabPane tab="Notifications" key="notifications">
            {renderNotificationsList()}
          </TabPane>

          <TabPane tab="Messages" key="messages">
            {renderMessagesList()}
          </TabPane>
        </Tabs>
      </Card>

      {/* Message Detail Drawer */}
      <Drawer
        title={selectedMessage?.subject || 'No Subject'}
        placement="right"
        onClose={() => setMessageDetailVisible(false)}
        open={messageDetailVisible}
        width={520}
      >
        {selectedMessage && (
          <MessageDetail 
            message={selectedMessage} 
            onReply={(messageId, replyText) => {
                messageService.replyToMessage(messageId, replyText)
                    .then(() => {
                    notification.success({
                        message: 'Reply Sent',
                        description: 'Your reply has been sent successfully'
                    });
                    setMessageDetailVisible(false);
                    })
                    .catch(error => {
                    console.error('Error sending reply:', error);
                    notification.error({
                        message: 'Failed to send reply',
                        description: error.message || 'Please try again later'
                    });
                    });
            }}
            onDelete={() => {
              deleteMessage(selectedMessage.id);
              setMessageDetailVisible(false);
            }}
          />
        )}
      </Drawer>
    </div>
  );
};

export default Inbox;